import { useEffect } from "react";
import { useGetApplicationSettings } from "services/common";
import { appLogout } from "store/auth-context";

function IdleManager() {
  const { data: appSettingsRes, mutate: getAppSettings } =
    useGetApplicationSettings();

  const CHECK_INTERVAL = 1000;
  const MINUTES_IN_MS = 60 * 1000;

  useEffect(() => {
    getAppSettings();
  }, []);

  const IDLE_TIMEOUT_MINUTES = Number(
    appSettingsRes?.data?.sessionTimeout || 10,
  );
  const IDLE_TIMEOUT = IDLE_TIMEOUT_MINUTES * MINUTES_IN_MS;

  useEffect(() => {
    // 1. Instantiating channel inside the hook to prevent multi-render memory leaks
    const channel = new BroadcastChannel("activityChannel");
    let interval;
    let lastActivity = Date.now();

    // eslint-disable-next-line no-console
    console.log(
      new Date(),
      "Idle tracking started. Timeout:",
      IDLE_TIMEOUT_MINUTES,
      "mins",
    );

    const updateActivity = () => {
      lastActivity = Date.now();
      channel.postMessage({ type: "USER_ACTIVITY", timestamp: lastActivity });
    };

    const triggerLogout = () => {
      // 2. CRITICAL: Stop the interval immediately so 'debugger' and 'appLogout' only execute once
      if (interval) clearInterval(interval);

      // eslint-disable-next-line no-console
      console.log(new Date(), "Logout due to Inactivity");

      appLogout();
    };

    const checkIdleState = () => {
      const now = Date.now();
      const allTabsIdle = now - lastActivity >= IDLE_TIMEOUT;

      if (allTabsIdle) {
        // Notify all other open tabs to log out
        channel.postMessage({ type: "ALL_TABS_IDLE" });
        triggerLogout();
      }
    };

    // Track user activity
    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keypress", updateActivity);
    window.addEventListener("click", updateActivity);

    // Broadcast initial activity state
    updateActivity();

    // Periodically check idle state
    interval = setInterval(checkIdleState, CHECK_INTERVAL);

    // Handle broadcast messages
    channel.onmessage = (event) => {
      if (event.data.type === "USER_ACTIVITY") {
        lastActivity = Math.max(lastActivity, event.data.timestamp);
      }
      // 3. Gracefully handle logout triggered by a different tab
      if (event.data.type === "ALL_TABS_IDLE") {
        triggerLogout();
      }
    };

    // Cleanup logic on unmount or when timeout changes
    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keypress", updateActivity);
      window.removeEventListener("click", updateActivity);
      channel.close();
    };

    // 4. Added IDLE_TIMEOUT dependency so timing changes update the event loop cleanly
  }, [IDLE_TIMEOUT, IDLE_TIMEOUT_MINUTES]);

  return null;
}

export default IdleManager;
